<template>
  <div class="installations" >
    <b-card
      v-if="regional.length > 0"
      header="Filtros"
      class="installations__filter"
    >
      <label>{{ $t('profile-manager.editing.installations.filter-title') }}</label>
      <s-multiselect
        v-model="filter"
        :proxy-binds="{
          options: regional,
          label: 'name',
          trackBy: 'id',
          multiple: true,
          preserveSearch: true,
        }"
      />
      <div class="installations__filter-footer">
        <b-button @click="clear" class="mb-2 mb-sm-0">
          {{ $t('profile-manager.editing.installations.clear-filter') }}
        </b-button>
        <b-button :disabled="!filter || !filter.length" @click="search" variant="primary">
          {{ $t('general.search') }}
        </b-button>
      </div>
    </b-card>

    <s-table
      class="installations__table no-align"
      :busy="loading"
      :fields="fields"
      :items="installations"
    >
      <template #head(check)>
        <div class="installations__check-wrapper">
          <b-form-checkbox @click.native.stop :checked="allSelected" @change="toggleAll"/>
        </div>
      </template>
      <template #cell(check)="data">
        <b-form-checkbox v-model="data.item.selected" />
      </template>
    </s-table>
  </div>
</template>

<script>
import { uniqBy } from 'lodash';
import RegionalService from '@/domain/services/regional';
import ProfileEditService from
    '@/domain/services/generalPanel/adminPanel/profile/profileEditService';

export default {
  components: {
  },
  props: {
    profile: Object,
  },
  data() {
    return {
      loading: true,
      filter: [],
      installations: [],
      regional: [],
    };
  },
  methods: {
    toggleAll(checked) {
      this.installations.forEach((it) => {
        it.selected = checked;
      });
    },
    clear() {
      this.filter = [];
      this.search();
    },
    async search() {
      this.loading = true;
      this.installations = await ProfileEditService
        .getProfileInstallations({
          profileId: this.profile.id,
          parentProfileId: this.profile.parentProfileId,
          regionalIds: this.regionalIds,
          companyIds: this.companyIds,
        });

      this.loading = false;

      if (this.profile.installations) {
        this.profile.installations.forEach(({ id: installationId }) => {
          const installation = this.installations.find(it => it.id === installationId);
          if (installation) {
            installation.selected = true;
          }
        });
      }
    },
  },
  computed: {
    allSelected() {
      return this.installations.length > 0 && this.installations.every(it => it.selected);
    },
    fields() {
      const prefix = 'profile-manager.editing.installations';

      return [
        { key: 'check', class: 'installations__cell-check' },
        {
          key: 'name',
          label: this.$t(`${prefix}.installation`),
          class: 'installations__cell-name',
        },
        {
          key: 'regionalName',
          label: this.$t(`${prefix}.regional`),
          class: 'installations__cell-regional-name',
        },
        {
          key: 'companyName',
          label: this.$t(`${prefix}.company`),
          class: 'installations__cell-company-name',
        },
      ];
    },
    regionalIds() {
      return this.filter.map(it => it.id);
    },
    companyIds() {
      return this.profile.companies.map(it => it.id);
    },
    selection() {
      return this.installations.filter(it => it.selected);
    },
  },
  created() {
    RegionalService.getRegional(this.companyIds).then((response) => {
      this.regional = response;
    });
    this.search().then(() => {
      this.$emit('setInitialValues:installations',
        this.installations.filter(installation => installation.selected));
    });
  },
  watch: {
    selection(items) {
      if (!this.loading) {
        const preservedItems = this.profile.installations.filter(oldItem => !this.installations
          .some(currentItem => currentItem.id === oldItem.id));
        this.profile.installations = uniqBy([...preservedItems, ...items], 'id');
      }
    },
  },
};
</script>

<style lang="sass" scoped>
  @import "src/styles/defaults/variables"
  @import "src/styles/defaults/mobile-first"

  .installations
    max-height: 485px
    display: flex
    flex-direction: column
    overflow-y: hidden
    &__table ::v-deep
      overflow-y: hidden
      display: flex
      table
        display: flex
        flex-direction: column
        min-width: 400px
      tbody
        display: block
        overflow-y: auto
        overflow-x: hidden
      tbody, thead
        tr
          display: table
          width: 100%
          table-layout: fixed
    &__loader
      font-size: 2em
      margin-bottom: 1em
    &__filter
      background-color: var(--color-background-light)
      display: block
      margin-bottom: 24px
      ::v-deep .card-header
        background-color: var(--color-background-dark)
    &__check-wrapper
      display: flex
    ::v-deep &__cell
      &-check
        width: 40px
      &-name,
      &-regional-name,
      &-company-name
        text-align: center
    &__filter-footer
      display: flex
      flex-direction: column
      padding: 16px 0
    +media-min-sm()
      &__filter-footer
        flex-direction: row
        justify-content: flex-end
        button:first-child
          margin-right: 16px
    +media-min-md()
    +media-min-lg()
</style>
